﻿import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/services/hooks";
import {
  getPageTypeOptions,
} from "@/services/categoryServices/categoryAction";
import { reportSoftFailure } from "@/layouts/Alerts";
import PageTypesUpdateView from "./PageTypesUpdateView";
import type { PageTypesUpdateViewProps } from "./PageTypesUpdateView";
import "./PageType.css";

/**
 * Warn when the saved page_configuration carries no usable object_target, so
 * the card is selected but nothing below it is highlighted.
 *
 * getPageById answers `page_configuration.object_target` as `null` when the
 * target no longer exists (server/controllers/page.js resolveTargetForEditor —
 * a deleted ref). The click handlers dereference it (`productWrapper.product._id`,
 * `post._id`, `author._id`, `category._id`), and this replay runs inside an
 * effect — an unguarded throw there takes the whole editor down instead of
 * degrading. Measured 2026-09-07 (SCRUM-1466), when the endpoint still nulled
 * every non-subcategory target: the AXA product page in the demo seed rendered
 * no page-type cards at all. `kind` names the target type in the message.
 */
function notifyPageTypeTargetUnusable(kind: string) {
  reportSoftFailure({
    code: `PAGE_TYPE_${kind.toUpperCase()}_TARGET_UNUSABLE`,
    message:
      `This page's saved ${kind} could not be loaded, so nothing is highlighted below. ` +
      `The page keeps its type — only pick one again if you want to change it.`,
    transient: false,
  });
}

// Sentinel for the per-page hydration latch below: it must differ from every
// real pageId AND from `undefined`, which is what a caller that passes none
// supplies.
const NOT_HYDRATED = Symbol("page-type-not-hydrated");

/** The page's stored page_configuration, as getPageById answers it (SCRUM-1651). */
type StoredPageConfiguration = {
  type?: string;
  path?: string;
  /** The projected target document, or null for a deleted ref. */
  object_target?: { _id?: unknown; product?: { _id?: unknown } | null } | null;
};

/** A product wrapper on a sub-category, as the page-type options answer it. */
type PickerProduct = { _id?: unknown; product: unknown };

/** A sub-category from the page-type options, with its product wrappers. */
type PickerSubcategory = { _id?: unknown; products: PickerProduct[] };

/** A page-type pick, as the update screen's container stores it (SCRUM-2216). */
export type PageTypeSelection = { type?: string; object?: unknown };

export type PageTypesUpdateContainerProps = {
  updateform?: boolean;
  /**
   * Receives every pick and the replay of the stored target. The parent owns
   * the selection (UpdatePageContainer state since SCRUM-2216; it was the
   * pages slice's page-type field, read back by screens without this step).
   * Its identity is listed in the replay's dependencies, so it must be stable.
   */
  onPageTypeChange: (selection: PageTypeSelection) => void;
  /** Latch key: the stored selection is replayed once per page. */
  pageId?: unknown;
  page_configuration?: StoredPageConfiguration | null;
};

export default function PageTypesUpdateContainer(props: PageTypesUpdateContainerProps) {
  const dispatch = useAppDispatch();
  const { onPageTypeChange } = props;

  // Redux state
  const Currentsite = useAppSelector((s) => s.sites.site);
  const SubCategoriesFromRedux = useAppSelector((s) => s.categories.pageTypeSubcategories);

  // Local state
  const [CurrentSite, setCurrentSite] = useState({});
  const [CategoryList, setCategoryList] = useState<any[]>([]); // any: SCRUM-2128 moves this state into a reducer
  const [SubCategoriesList, setSubCategoriesList] = useState<any[]>([]); // any: SCRUM-2128 moves this state into a reducer
  const [ProductsList, setProductsList] = useState<any[]>([]); // any: SCRUM-2128 moves this state into a reducer
  const [checker, setChecker] = useState({
    simple_page: false,
    category: false,
    subcategory: false,
    product: false,
    posts: false,
    authors: false,
  });
  const [requiresChecker, setRequiresChecker] = useState("");
  const [pathSimplePage, setPathSimplePage] = useState("");
  const [radioGroupValue, setRadioGroupValue] = useState("default");
  const [blogAvailability, setBlogAvailability] = useState(false);
  const [postsAvailability, setPostsAvailability] = useState(false);
  const [authors, setAuthors] = useState<any[]>([]); // any: SCRUM-2128 moves this state into a reducer

  // Helpers
  const getUnique = useCallback(<T,>(arr: T[] | null | undefined, comp: string): T[] => {
    if (!Array.isArray(arr)) return [];
    const ids = new Set();
    const out: T[] = [];
    for (const item of arr) {
      const key = (item as Record<string, unknown> | null)?.[comp];
      if (key != null && !ids.has(key)) {
        ids.add(key);
        out.push(item);
      }
    }
    return out;
  }, []);

  const extractProductsFromSubcats = useCallback((subcats: PickerSubcategory[] | null | undefined) => {
    const subcategories = getUnique(subcats || [], "_id");
    const subcategoriesProducts = subcategories.map((sc) => sc.products).flat();
    const products = getUnique(subcategoriesProducts || [], "_id").filter(
      (obj) => obj.product !== null
    );
    return { subcategories, products };
  }, [getUnique]);

  // SCRUM-1734: `site.blog` is the blog's id on the current-site response
  // (getCurrentSite never populated it, and the Blog schema no longer carries
  // a `posts` array), so the authors/posts pickers this used to derive from
  // the blog's posts array never had input on this screen. The availability flags
  // keep the values the real response produced: a blog id means "the site
  // has a blog", and there is no post list to pick from here.
  const prepareAuthors = useCallback((site) => {
    setBlogAvailability(Boolean(site?.blog));
    setPostsAvailability(false);
    setAuthors([]);
  }, []);

  // Handlers (parent callback + local state)
  const onChangeRadioInputHandler = useCallback((e) => {
    const val = e.currentTarget.value;
    let mapped = "";
    if (val === "error") mapped = "404";
    else if (val === "maintenance") mapped = "maintenance";
    else if (val === "redirect") mapped = "redirect";

    onPageTypeChange({ type: val, object: mapped });
    setRadioGroupValue(val);
    setPathSimplePage(mapped);
  }, [onPageTypeChange]);

  const onChangeSimplePageInputHandler = useCallback((e) => {
    onPageTypeChange({ type: "simple_page", object: e.target.value });
    setPathSimplePage(e.target.value);
  }, [onPageTypeChange]);

  const onClickProductHandler = useCallback((productWrapper) => {
    onPageTypeChange({ type: "product", object: productWrapper });
    setRequiresChecker(productWrapper.product._id);
  }, [onPageTypeChange]);

  const onClickCategoryHandler = useCallback((category) => {
    onPageTypeChange({ type: "category", object: category });
    setRequiresChecker(category._id);
  }, [onPageTypeChange]);

  const onClickSubCategoryHandler = useCallback((subcat) => {
    onPageTypeChange({ type: "sub_category", object: subcat });
    setRequiresChecker(subcat._id);
  }, [onPageTypeChange]);

  const onClickPostHandler = useCallback((post) => {
    onPageTypeChange({ type: "post", object: post });
    setRequiresChecker(post._id);
  }, [onPageTypeChange]);

  const onClickAuthorHandler = useCallback((author) => {
    onPageTypeChange({ type: "author", object: author });
    setRequiresChecker(author._id);
  }, [onPageTypeChange]);

  const selectType = useCallback((name) => {
    setChecker({
      simple_page: false,
      category: false,
      subcategory: false,
      product: false,
      posts: false,
      authors: false,
      [name]: true,
    });
  }, []);

  // Effects: initial + Currentsite changes
  useEffect(() => {
    if (Currentsite) {
      setCurrentSite(Currentsite);
      const cats = Currentsite.categories || [];
      setCategoryList(cats);
      prepareAuthors(Currentsite);
    }
  }, [Currentsite, prepareAuthors]);

  // Effect: when we have SubCategories from Redux, compute products
  useEffect(() => {
    if (SubCategoriesFromRedux && SubCategoriesFromRedux.length >= 0) {
      const { subcategories, products } = extractProductsFromSubcats(SubCategoriesFromRedux);
      setSubCategoriesList(subcategories);
      setProductsList(products);
    }
  }, [SubCategoriesFromRedux, extractProductsFromSubcats]);

  // Effect: one request for the whole picker (SCRUM-1653 slice 2). This feeds
  // the PICKER only — SubCategoriesList and ProductsList, the cards the editor
  // can click (PageTypesUpdateView.jsx). The stored selection does not wait on
  // it: getPageById hands the full target (SCRUM-1651), so the SCRUM-1637 settle
  // latch that watched the old per-category fetches is gone with them.
  useEffect(() => {
    if (Currentsite?._id) dispatch(getPageTypeOptions());
  }, [Currentsite?._id, dispatch]);


 
  const prepareForUpdateCall = useCallback(() => {
    const { subcategories, products } = extractProductsFromSubcats(SubCategoriesFromRedux || []);
    setCurrentSite(Currentsite || {});
    setCategoryList(Currentsite?.categories || []);
    setSubCategoriesList(subcategories);
    setProductsList(products);

    const oldCfg = props.page_configuration;
    if (!oldCfg) return;

    // Simulate previous event-based API
    if (oldCfg.type === "simple_page" || oldCfg.type === "") {
      selectType("simple_page");
      setPathSimplePage(oldCfg.path || "");
      setRadioGroupValue("default");
      onPageTypeChange({ type: "simple_page", object: oldCfg.path || "" });
      return;
    }

    if (oldCfg.type === "error" || oldCfg.type === "maintenance" || oldCfg.type === "redirect") {
      selectType("simple_page");
      // reuse the radio handler flow
      setRadioGroupValue(oldCfg.type);
      const mapped = oldCfg.type === "error" ? "404" : oldCfg.type;
      setPathSimplePage(mapped);
      onPageTypeChange({ type: oldCfg.type, object: mapped });
      return;
    }

    // product / category / sub_category: getPageById answers `object_target` as
    // the projected document the editor reads (server/controllers/page.js
    // resolveTargetForEditor, SCRUM-1651) — `product._id` and `path` for a
    // product, `slug` + `subcategories[].products` for a category, `slug` +
    // `category.slug` + `products[]` for a subcategory — so the stored target
    // is replayed as a live click would pass it, with no lookup in the picker
    // lists. Until SCRUM-1651 the endpoint sent a slim { _id } that had to be
    // hydrated from those lists, which produced SCRUM-1466, SCRUM-1550 and
    // SCRUM-1637. A null target (deleted ref) degrades to the unusable notice;
    // the write boundary (refusePageTypePayload in the parent's handler,
    // SCRUM-1650 / SCRUM-2216) stays as the safety net should a payload ever
    // arrive without its array.
    if (oldCfg.type === "product") {
      selectType("product");
      // onClickProductHandler dereferences `.product._id`.
      if (oldCfg.object_target?.product?._id) {
        onClickProductHandler(oldCfg.object_target);
      } else {
        notifyPageTypeTargetUnusable("product");
      }
      return;
    }
    if (oldCfg.type === "category") {
      selectType("category");
      if (oldCfg.object_target?._id) {
        onClickCategoryHandler(oldCfg.object_target);
      } else {
        notifyPageTypeTargetUnusable("category");
      }
      return;
    }
    if (oldCfg.type === "sub_category") {
      selectType("subcategory");
      if (oldCfg.object_target?._id) {
        onClickSubCategoryHandler(oldCfg.object_target);
      } else {
        notifyPageTypeTargetUnusable("subcategory");
      }
      return;
    }
    if (oldCfg.type === "post") {
      selectType("posts");
      if (oldCfg.object_target?._id) {
        onClickPostHandler(oldCfg.object_target);
      } else {
        notifyPageTypeTargetUnusable("post");
      }
      return;
    }
    if (oldCfg.type === "author") {
      selectType("authors");
      if (oldCfg.object_target?._id) {
        onClickAuthorHandler(oldCfg.object_target);
      } else {
        notifyPageTypeTargetUnusable("author");
      }
    }
  }, [
    Currentsite,
    SubCategoriesFromRedux,
    extractProductsFromSubcats,
    props.page_configuration,
    selectType,
    onPageTypeChange,
    onClickProductHandler,
    onClickCategoryHandler,
    onClickSubCategoryHandler,
    onClickPostHandler,
    onClickAuthorHandler,
  ]);

  // The update screen's page arrives asynchronously (UpdatePageContainer
  // dispatches getPageById on mount and renders with page = {} until it
  // resolves), so this effect must key on the configuration itself, not on the
  // constant `updateform` flag: keyed on the flag it ran once, at mount, when
  // page_configuration was still undefined and prepareForUpdateCall returned
  // immediately — leaving every page type unchecked on an existing page.
  //
  // One condition before hydrating, and a ref so it happens exactly once (a
  // second run would fight the user's own clicks: each selection round-trips
  // through the parent's state and hands this component a fresh
  // page_configuration object).
  // Latched per PAGE, not per component instance. `/update-pg/:id` is one route
  // whose element is keyed by the route PATTERN (AdminContainer.jsx:156
  // `key={r.path}`), so moving from update-pg/A to update-pg/B re-renders this
  // component instead of remounting it. A boolean latch survives that and would
  // leave page B showing page A's selected card, never hydrating — measured by
  // lens B in the SCRUM-1466 double check (`C4_same_mount_pageB_checker`).
  // NOT_HYDRATED rather than undefined: a caller that passes no pageId (the unit
  // tests) must still latch after its single hydration.
  //
  // The configuration is all the latch waits for. Until SCRUM-1651 it also
  // waited for the per-category subcategory fetches, because the slim target
  // had to be looked up in them; the target now arrives complete from
  // getPageById, so the stored card is selected on the first render after
  // page_configuration lands, with every subcategory fetch still outstanding.
  const hydratedForRef = useRef<unknown>(NOT_HYDRATED);
  useEffect(() => {
    if (!props.updateform || hydratedForRef.current === props.pageId) return;

    // Wait for the real configuration. Before getPageById resolves the parent
    // still passes a placeholder derived from an empty page_type
    // ({} -> { type: undefined }), which carries nothing to select. A saved
    // "default" page legitimately has type "", hence the string test rather
    // than a truthiness one.
    const cfg = props.page_configuration;
    if (typeof cfg?.type !== "string") return;

    hydratedForRef.current = props.pageId;
    prepareForUpdateCall();
  }, [props.updateform, props.pageId, props.page_configuration, prepareForUpdateCall]);

  // Memo: view props 
  const viewProps = useMemo(
    (): PageTypesUpdateViewProps => ({
      // state
      checker,
      requiresChecker,
      pathSimplePage,
      radioGroupValue,
      blogAvailability,
      postsAvailability,
      authors,
      CurrentSite,
      CategoryList,
      SubCategoriesList,
      ProductsList,

      // handlers
      selectType,
      onChangeSimplePageInputHandler,
      onChangeRadioInputHandler,
      onClickCategoryHandler,
      onClickSubCategoryHandler,
      onClickProductHandler,
      onClickPostHandler,
      onClickAuthorHandler,
    }),
    [
      checker,
      requiresChecker,
      pathSimplePage,
      radioGroupValue,
      blogAvailability,
      postsAvailability,
      authors,
      CurrentSite,
      CategoryList,
      SubCategoriesList,
      ProductsList,
      selectType,
      onChangeSimplePageInputHandler,
      onChangeRadioInputHandler,
      onClickCategoryHandler,
      onClickSubCategoryHandler,
      onClickProductHandler,
      onClickPostHandler,
      onClickAuthorHandler,
    ]
  );

  return <PageTypesUpdateView {...viewProps} />;
}
